import React, { useMemo, useState } from 'react'
import {
  ColorSelect,
  COLOR_SELECT_PREVIEW_STATE_LABELS,
  COLOR_SELECT_PREVIEW_STATES,
  type ColorSelectPreviewState,
  type DesignSystemColorFamily,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './ColorSelectShowcase.css'

const ColorSelectShowcase: React.FC = () => {
  const [color, setColor] = useState<DesignSystemColorFamily | ''>('')
  const [label, setLabel] = useState('Color')
  const [disabled, setDisabled] = useState(false)
  const [optional, setOptional] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [error, setError] = useState(false)
  const [helperText, setHelperText] = useState('')
  const [highlightedState, setHighlightedState] =
    useState<ColorSelectPreviewState>('default')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'color-select-highlight-state',
            label: 'Highlighted state',
            control: (
              <select
                id="color-select-highlight-state"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as ColorSelectPreviewState,
                  )
                }
              >
                {COLOR_SELECT_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {COLOR_SELECT_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'color-select-label',
            label: 'Label',
            control: (
              <input
                id="color-select-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
          {
            id: 'color-select-helper-text',
            label: 'Helper text',
            control: (
              <input
                id="color-select-helper-text"
                className="ui-field-control"
                type="text"
                value={helperText}
                placeholder="Optional helper text"
                onChange={(event) => setHelperText(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'color-select-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="color-select-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'color-select-optional',
            label: 'Optional',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="color-select-optional"
                  type="checkbox"
                  checked={optional}
                  onChange={(event) => setOptional(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'color-select-tooltip',
            label: 'Tooltip',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="color-select-tooltip"
                  type="checkbox"
                  checked={showTooltip}
                  onChange={(event) => setShowTooltip(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'color-select-error',
            label: 'Error',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="color-select-error"
                  type="checkbox"
                  checked={error}
                  onChange={(event) => setError(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [
      disabled,
      error,
      helperText,
      highlightedState,
      label,
      optional,
      showTooltip,
    ],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Color select"
        description="Select a design-system color family from a swatch list, matching Select field chrome."
        stageClassName="ui-preview__stage--wide color-select-showcase__stage-wrap"
      >
        <div className={`color-select-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Choose a design-system color family. Each option shows a swatch
              beside its name.
            </p>
            <div className="color-select-showcase__live-grid">
              <ColorSelect
                label={label}
                value={color}
                disabled={disabled}
                optional={optional}
                showTooltip={showTooltip}
                error={error}
                helperText={helperText || undefined}
                onChange={setColor}
              />
            </div>
          </section>

          <section className="color-select-showcase__matrix">
            {COLOR_SELECT_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`color-select-showcase__row${
                  previewState === highlightedState
                    ? ' color-select-showcase__row--selected'
                    : ''
                }${
                  previewState === 'expanded'
                    ? ' color-select-showcase__row--expanded'
                    : ''
                }`}
              >
                <h4 className="color-select-showcase__label">
                  {COLOR_SELECT_PREVIEW_STATE_LABELS[previewState]}
                </h4>
                <ColorSelect previewState={previewState} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Color select"
        sections={propertySections}
      />
    </>
  )
}

export default ColorSelectShowcase
